import { poundsToGrams } from "@redotech/redo-fulfillment-measurements-common/weight-conversion";
import {
  MerchantAppReturn,
  productRequiresShipping,
  ReturnAddress,
} from "@redotech/redo-return-core-common/return";

export interface CorrectedAddressInput {
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  phone?: string | null;
}

export interface LabelCorrections {
  shipmentId?: string | null;
  correctedFromAddress?: CorrectedAddressInput | null;
  correctedToAddress?: CorrectedAddressInput | null;
  correctedWeightLb?: number | null;
}

function applyAddress(
  address: ReturnAddress,
  fix: CorrectedAddressInput,
): ReturnAddress {
  return {
    ...address,
    name: fix.name ?? address.name,
    address1: fix.street1 ?? address.address1,
    address2: fix.street2 ?? address.address2,
    city: fix.city ?? address.city,
    province: fix.state ?? address.province,
    zip: fix.zip ?? address.zip,
    country: fix.country ?? address.country,
    phone: fix.phone ?? address.phone,
  };
}

function correctedProducts(
  return_: MerchantAppReturn,
  correctedWeightLb: number | null | undefined,
) {
  if (correctedWeightLb == null || correctedWeightLb <= 0) {
    return return_.products;
  }
  const shippedQuantity = (return_.products ?? [])
    .filter(productRequiresShipping)
    .reduce((total, product) => total + (product.quantity ?? 0), 0);
  if (shippedQuantity <= 0) {
    return return_.products;
  }
  const perUnitGrams = poundsToGrams(correctedWeightLb) / shippedQuantity;
  return (return_.products ?? []).map((product) => ({
    ...product,
    grams: perUnitGrams,
  }));
}

function groupIdForShipment(
  return_: MerchantAppReturn,
  shipmentId: string | null | undefined,
): string | undefined {
  if (!shipmentId) {
    return undefined;
  }
  const shipment = [
    ...(return_.shipments ?? []),
    ...(return_.shipment ? [return_.shipment] : []),
  ].find((candidate) => candidate._shipment?.id === shipmentId);
  return shipment?.shipmentGroupID ?? undefined;
}

function correctedShipmentGroups(
  return_: MerchantAppReturn,
  correctedToAddress: CorrectedAddressInput | null | undefined,
  targetGroupId: string | undefined,
): MerchantAppReturn["shipmentGroups"] {
  if (!correctedToAddress || !return_.shipmentGroups?.length) {
    return return_.shipmentGroups;
  }
  return return_.shipmentGroups.map((group) =>
    !targetGroupId || group.id === targetGroupId
      ? { ...group, address: applyAddress(group.address, correctedToAddress) }
      : group,
  );
}

export function applyLabelCorrections(
  return_: MerchantAppReturn,
  corrections: LabelCorrections,
): MerchantAppReturn {
  const { correctedFromAddress, correctedToAddress, correctedWeightLb } =
    corrections;

  const shippingAddress =
    return_.shipping_address && correctedFromAddress
      ? applyAddress(return_.shipping_address, correctedFromAddress)
      : return_.shipping_address;

  const merchantAddress =
    return_.merchant_address && correctedToAddress
      ? applyAddress(return_.merchant_address, correctedToAddress)
      : return_.merchant_address;

  const shipmentGroups = correctedShipmentGroups(
    return_,
    correctedToAddress,
    groupIdForShipment(return_, corrections.shipmentId),
  );

  return {
    ...return_,
    products: correctedProducts(return_, correctedWeightLb),
    shipping_address: shippingAddress,
    merchant_address: merchantAddress,
    shipmentGroups,
  } as MerchantAppReturn;
}
